import { canonicalizeLocale } from './canonicalize';
import { createResolver } from './resolve';
import {
  I18N_FALLBACK_LOCALE,
  LOCALE_TRANSLATION_MAP,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from './constants';
import type { LocaleCode, ResolvedLocale } from './types';

// Module-scope resolver: the canonical lookup map for the registry is built
// once, not on every resolveSupportedLocale call.
const resolveAgainstRegistry = createResolver(
  SUPPORTED_LOCALES,
  I18N_FALLBACK_LOCALE,
);

/**
 * Resolves a locale input to a canonical SupportedLocale from the Joy registry,
 * along with recognition metadata (`recognized`, `matchType`, the raw `input`).
 *
 * This is the single source of truth for alias-aware resolution — both
 * `resolveSupportedLocale` (locale-only convenience) and any consumer that
 * needs the full result should go through this function.
 *
 * Handles, in order:
 * 1. Explicit aliases in LOCALE_TRANSLATION_MAP (e.g. 'en-IE' → 'en-GB', 'en-NZ' → 'en-AU').
 *    Consulted BEFORE the generic resolver because the generic sibling matcher would
 *    otherwise pick 'en-US' as the closest English variant for 'en-IE' — the map
 *    encodes product intent. Input is canonicalized first ('en-nz' → 'en-NZ') so
 *    non-canonical casing that passed BCP 47 format validation on write still hits the map.
 *    Alias matches are synthesized as `recognized: true` — the input mapped to a
 *    real, deliberate product choice, not a fallback — with `input` set to the
 *    RAW pre-normalization string (matching the semantics `createResolver` uses
 *    for every other match type), not the canonicalized intermediate.
 * 2. Everything else — bare codes ('en' → 'en-US'), regional variants ('de-AT' → 'de-DE'),
 *    case/separator normalization ('DE-de' → 'de-DE', 'en_US' → 'en-US'),
 *    null/undefined → 'en-US' (fallback) — returned whole from `resolveAgainstRegistry`,
 *    which already produces the correct `recognized`/`matchType`/`input` shape.
 *
 * Consumers that need to distinguish "we guessed" from "we matched" — e.g.
 * joy-web's `useGeoLocale`, which must not silently force the fallback locale
 * on unrecognized input — should read `.recognized` rather than comparing
 * `.locale` against the fallback (a real user could legitimately request the
 * fallback locale itself).
 *
 * Works identically in browser and Node — no environment-specific dependencies.
 */
export function resolveSupportedLocaleResult(
  locale: string | null | undefined,
): ResolvedLocale {
  const canonical = locale ? canonicalizeLocale(locale) : null;
  // hasOwnProperty.call instead of Object.hasOwn: the repo's engine floor is
  // Node >=16, and Object.hasOwn only exists from 16.9 onward.
  if (
    canonical &&
    Object.prototype.hasOwnProperty.call(LOCALE_TRANSLATION_MAP, canonical)
  ) {
    return {
      locale: LOCALE_TRANSLATION_MAP[canonical] as LocaleCode,
      recognized: true,
      input: locale ?? null,
      matchType: 'alias',
    };
  }
  return resolveAgainstRegistry(locale);
}

/**
 * Resolves a locale input to a canonical SupportedLocale from the Joy registry.
 * Convenience wrapper over `resolveSupportedLocaleResult` for callers that only
 * need the resolved locale — see that function's JSDoc for the full resolution
 * contract (alias precedence, recognized/matchType semantics).
 */
export function resolveSupportedLocale(
  locale: string | null | undefined,
): SupportedLocale {
  return resolveSupportedLocaleResult(locale).locale as SupportedLocale;
}

/**
 * @deprecated Renamed to `resolveSupportedLocale` in 2.x — this alias will be
 * removed in 3.0.0. Signature-compatible with the `resolveEmailLocale`
 * exported by `@withjoy/locale@2.1.0` (same `string | null | undefined ->
 * SupportedLocale` shape); kept so 2.1.0 consumers importing the package
 * export directly (not a local wrapper) don't break on a minor bump.
 */
export const resolveEmailLocale = resolveSupportedLocale;
